'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@/app/context/toast-provider';
import { ERROR_MESSAGES } from '@/app/utils/errorMessage';
import { generateInterviewQuestionsRequest } from '@/app/utils/handlers/client/interview';
import { requestHandler } from '@/app/utils/requestHandler';
import { useConfirmDialog } from '../useConfirmDialog/useConfirmDialog';

/** How long the blocking backdrop waits for the questions-generated event before giving up. */
const WAIT_FOR_QUESTIONS_MS = 10 * 60 * 1000;
/**
 * Creating an AI interview: asks first (nothing is sent to the candidate), then asks the server to generate the
 * questions for the round. The request returns at once; the questions arrive later through the
 * `SCREENING_INTERVIEW_QUESTIONS_GENERATED` event, which `useInterviewData` hands to `clearWaitingForQuestions`.
 */
export function useGenerateScreeningQuestions({
  interview,
  setInterview,
  setAssessmentTemplate,
  emailRef,
  companyScreeningVariant,
  candidatePhoneNumber,
  refreshJobApplicationList,
  setRequestLoading,
  blockingBackdrop,
  onGenerated
}) {
  const {
    confirm
  } = useConfirmDialog();
  const {
    showErrorToast
  } = useToast();
  const [transport, setTransport] = useState('DAILY_JS');
  const [mode, setMode] = useState('WEB');
  const [showScreeningBackdrop, setShowScreeningBackdrop] = useState(false);
  const waitingTimeoutRef = useRef(null);

  // The interview's own transport and mode win; otherwise the company's default transport.
  useEffect(() => {
    const interviewTransport = interview?.ScreeningInterview?.interviewTransport;
    if (interviewTransport) {
      setTransport(interviewTransport);
    } else if (companyScreeningVariant === 'FASTRTC' || companyScreeningVariant === 'DAILY_JS') {
      setTransport(companyScreeningVariant);
    }
    if (interview?.ScreeningInterview?.mode) {
      setMode(interview.ScreeningInterview.mode);
    }
  }, [interview?.ScreeningInterview?.interviewTransport, interview?.ScreeningInterview?.mode, companyScreeningVariant]);
  const clearWaitingForQuestions = useCallback(() => {
    setShowScreeningBackdrop(false);
    if (waitingTimeoutRef.current) {
      clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => clearWaitingForQuestions, [clearWaitingForQuestions]);
  const generate = interviewRound => requestHandler(async () => await generateInterviewQuestionsRequest({
    roundNum: interviewRound.roundNum,
    interviewTransport: transport,
    mode,
    candidatePhoneNumber: candidatePhoneNumber
  }, {
    interviewId: interview.id
  }), res => {
    const {
      data
    } = res;
    setInterview({
      ...data,
      email: interview?.email || emailRef?.current
    });
    if (data?.assessmentTemplate) {
      setAssessmentTemplate(data.assessmentTemplate);
    }
    if (blockingBackdrop) {
      setShowScreeningBackdrop(true);
      waitingTimeoutRef.current = setTimeout(() => {
        setShowScreeningBackdrop(false);
        showErrorToast('Question generation is taking longer than expected. Please refresh the page.');
      }, WAIT_FOR_QUESTIONS_MS);
    }
    const updatedInterviewRound = data?.InterviewRounds?.find(ir => ir.id === interviewRound.id);
    onGenerated?.({
      interviewRound,
      ...updatedInterviewRound
    });
    refreshJobApplicationList?.();
  }, errMessage => {
    showErrorToast(errMessage);
    setShowScreeningBackdrop(false);
  }, setRequestLoading ?? null);

  /** Asks before generating; a round without a number can't be generated. */
  const confirmGenerate = interviewRound => {
    if (interviewRound?.roundNum === 0) {
      showErrorToast(ERROR_MESSAGES.INTERVIEW_ROUND_NUMBER_NOT_SELECTED);
      setRequestLoading?.(false);
      return;
    }
    const candidateName = interview?.firstName && interview?.lastName ? `${interview.firstName} ${interview.lastName}` : interview?.firstName || interview?.lastName || 'the candidate';
    confirm({
      title: 'Generate AI Interview',
      confirmButtonText: 'Generate',
      color: 'brand',
      content: <div className="flex flex-col gap-2">
                    <p>
                        <strong>Confirmation:</strong> Are you sure you want to create an AI Interview for <strong>{candidateName}</strong>?
                    </p>
                    <p className="text-sm text-foreground-muted">
                        This will generate interview questions based on the job requirements and assessment parameters.
                    </p>
                    <p className="text-sm text-blue-600 font-medium">
                        Note: No email will be sent to the candidate. You can review the questions and click &ldquo;Send Link&rdquo; when ready.
                    </p>
                </div>,
      onConfirm: () => generate(interviewRound)
    });
  };
  return {
    confirmGenerate,
    generate,
    showScreeningBackdrop,
    clearWaitingForQuestions
  };
}